import { Request } from '@transsion/request'

import {
  HEADER_AUTH,
  HEADER_EMP_NO,
  HEADER_LANGID,
  HEADER_REQUEST_ID,
  HEADER_RTOKEN,
  HEADER_SYS_CODE,
  PERM_ALL_RESOURCES_PATH,
  PERM_PERMISSIONS_PATH,
  PERM_USER_ROLES_PATH,
  PERM_USER_ROLES2_PATH,
} from './constants'
import { getDefaultBaseUrl, getDefaultStorePrefixKey, getFallbackLanguage } from './defaults'
import { ResourceType } from './enums'
import { InvalidEmployeeNoError, InvalidRequestTokenError, InvalidUserTokenError } from './errors'
import type { LanguageHolder, Permission, PermissionStorage } from './interfaces'
import { normalizeEnv, normalizeLanguageGetter, normalizeRequestId } from './normalize'
import InternalStorage from './storage'
import type {
  BaseUrlCallback,
  BasicResourceItem,
  CompareFn,
  Env,
  LangCallback,
  PermConfig,
  PermissionCodeCallback,
  RequestIdCallback,
  ResourceItem,
  ResRequestOptions,
  RoleItem,
  RouteFilterOptions,
  ServerLocation,
  ServiceType,
} from './types'
import { filterAndFlatResources, filterResourceTree } from './utils'

/**
 * 初始化permission参数
 */
export interface Options {
  /** 环境变量 */
  env?: Env
  /** 服务器地址 */
  location?: ServerLocation
  /** 获取BaseUrl */
  baseUrl?: string | BaseUrlCallback
  /** 应用配置 */
  appConfig: PermConfig
  /** 请求ID，用于跟踪用户请求 */
  requestId?: string | RequestIdCallback
  /** 客户端语言 */
  lang?: string | LangCallback | Partial<Pick<LanguageHolder, 'getLang'>>
  /** 存储 Token 的 localStorage Key 前缀 */
  storagePrefixKey?: string
  /** 自定义 Storage */
  storage?: PermissionStorage
}

/** 用户名下功能权限返回data */
interface FunctionalData {
  appCode: string
  iscUiResource4AuthVoList: ResourceItem[] | null
}

/** 用户名下所有资源权限返回data */
interface ResourceData {
  appCode: string
  reCodeList: string[]
}

export default class Perm implements Permission {
  readonly serviceType: ServiceType = 'perm'

  /** 应用配置 */
  #appConfig: PermConfig

  /** 基本链接 */
  #baseUrl: string

  /** 语言 */
  #languageGetter: LangCallback

  /** 储存 */
  #storage: PermissionStorage

  #request: Request

  constructor(opts: Options) {
    if (!opts.appConfig?.permCode || !opts.appConfig.permKey) {
      throw new TypeError(
        `The ${this.serviceType} service requires appConfig.permCode and appConfig.permPassword`,
      )
    }

    const env = normalizeEnv(opts.env)

    this.#baseUrl =
      typeof opts.baseUrl === 'function'
        ? opts.baseUrl(this.serviceType, env, opts.location)
        : (opts.baseUrl ?? getDefaultBaseUrl(this.serviceType, env, opts.location))

    this.#appConfig = opts.appConfig

    if (opts.storage) {
      this.#storage = opts.storage
    } else {
      const storagePrefixKey =
        opts.storagePrefixKey ?? getDefaultStorePrefixKey(opts.appConfig.permCode)

      this.#storage = new InternalStorage({
        prefixKey: storagePrefixKey,
      })
    }

    this.#languageGetter = normalizeLanguageGetter(opts.lang)

    const req = new Request({
      baseURL: this.#baseUrl,
      headers: {
        [HEADER_REQUEST_ID]: normalizeRequestId(opts.requestId),
      },
    })

    // 响应拦截
    req.addResponseInterceptors(({ data }) => {
      if (data?.code !== '200' || !data.data) {
        throw new Error(data?.message || 'Invalid response')
      }

      return data.data
    })

    this.#request = req
  }

  /**
   * 获取用户在应用下的功能权限, 涵盖模块、菜单、按钮、网页
   *
   * @param opts {Object} 参数
   * @returns {Promise<ResourceItem[]>} 返回功能权限列表
   */
  public async getFunctionalPermissions(opts?: ResRequestOptions): Promise<ResourceItem[]> {
    if (!opts && typeof this.#storage.getFunctionalPermissions === 'function') {
      const list = this.#storage.getFunctionalPermissions()

      if (list) return list
    }

    const params = this.#getRequestParams(opts)

    const { iscUiResource4AuthVoList } = await this.#request.post<FunctionalData>(
      PERM_PERMISSIONS_PATH,
      {
        lang: params.lang,
        jobNumber: params.employeeNo,
        appCode: this.#appConfig.permCode,
        password: this.#appConfig.permKey,
      },
      {
        headers: {
          [HEADER_AUTH]: params.rtoken,
          [HEADER_EMP_NO]: params.employeeNo,
          [HEADER_LANGID]: params.lang,
          [HEADER_SYS_CODE]: this.#appConfig.permCode,
          [HEADER_RTOKEN]: params.utoken,
        },
      },
    )

    const data = iscUiResource4AuthVoList ?? []

    if (!opts && typeof this.#storage.setFunctionalPermissions === 'function') {
      this.#storage.setFunctionalPermissions(data)
    }

    return data
  }

  /**
   * 获取用户在应用下的菜单权限树
   * 规则：如果父节点不是菜单类型，过滤的时候会直接去除该节点，不论下面的子节点中是否有菜单选项
   * 统一权限菜单配置规则：菜单嵌套菜单
   *
   * @param {Object} opts 参数
   * @returns {Promise<ResourceItem[]>} 菜单权限树
   */
  public async getMenuTree(opts?: ResRequestOptions, moduleCode?: string): Promise<ResourceItem[]> {
    const permissions = await this.getFunctionalPermissions(opts)

    if (moduleCode) {
      const module = permissions.find(
        (item) => item.reType === ResourceType.MODULE && item.reCode === moduleCode,
      )

      if (module) {
        return filterResourceTree(module.childList || [], ResourceType.MENU)
      }

      return []
    }

    return filterResourceTree(permissions, ResourceType.MENU)
  }

  /**
   * 获取用户在应用下的功能权限
   * 规则：过滤所需要的权限类型，返回一个扁平结构的列表，无层级结构
   *
   * @param {ResourceType} 权限类型 01:模块 02:菜单 03:按钮 04:网页，默认 02
   * @param {Object} opts 参数
   * @returns {Promise<BasicResourceItem[]>} 返回功能权限列表
   */
  public async getResourceList(
    resourceType: ResourceType,
    opts?: ResRequestOptions,
    moduleCode?: string,
  ): Promise<BasicResourceItem[]> {
    const permissions = await this.getFunctionalPermissions(opts)

    if (moduleCode) {
      const module = permissions.find(
        (item) => item.reType === ResourceType.MODULE && item.reCode === moduleCode,
      )

      if (module) {
        return filterAndFlatResources(module.childList || [], resourceType || ResourceType.MENU)
      }

      return []
    }

    return filterAndFlatResources(permissions, resourceType || ResourceType.MENU)
  }

  /**
   * 获取用户在应用下的所有资源列表
   *
   * @param {Object} opts 参数
   * @returns {Promise<string[]>} 返回所有资源列表
   */
  public async getAllResourceCodeList(opts?: ResRequestOptions): Promise<string[]> {
    if (!opts && typeof this.#storage.getResourceCodeList === 'function') {
      const list = this.#storage.getResourceCodeList()

      if (list) return list
    }

    const params = this.#getRequestParams(opts)

    const { reCodeList } = await this.#request.post<ResourceData>(
      PERM_ALL_RESOURCES_PATH,
      {
        lang: params.lang,
        jobNumber: params.employeeNo,
        appCode: this.#appConfig.permCode,
        password: this.#appConfig.permKey,
      },
      {
        headers: {
          [HEADER_AUTH]: params.rtoken,
          [HEADER_EMP_NO]: params.employeeNo,
          [HEADER_LANGID]: params.lang,
          [HEADER_SYS_CODE]: this.#appConfig.permCode,
          [HEADER_RTOKEN]: params.utoken,
        },
      },
    )

    const data = reCodeList || []

    if (!opts && typeof this.#storage.setResourceCodeList === 'function') {
      this.#storage.setResourceCodeList(data)
    }

    return data
  }

  #getEmployeeNo(defaultEmployeeNo?: string): string {
    if (defaultEmployeeNo) return defaultEmployeeNo

    let employeeNo = this.#storage.getEmployeeNo?.()

    if (!employeeNo) {
      const userInfo = this.#storage.getUserInfo()

      employeeNo = userInfo?.employeeNo
    }

    return employeeNo ?? ''
  }

  #getRequestParams(
    opts?: ResRequestOptions,
  ): Record<'rtoken' | 'utoken' | 'employeeNo' | 'lang', string> {
    const rtoken = opts?.rtoken || this.#storage.getRtoken()
    const utoken = opts?.utoken || this.#storage.getUtoken()

    const employeeNo = this.#getEmployeeNo(opts?.employeeNo)

    if (!rtoken) {
      throw InvalidRequestTokenError
    }

    if (!utoken) {
      throw InvalidUserTokenError
    }

    if (!employeeNo) {
      throw InvalidEmployeeNoError
    }

    const lang = this.#languageGetter() ?? getFallbackLanguage()

    return { rtoken, utoken, employeeNo, lang }
  }

  /**
   * 判断用户是否有菜单权限
   *
   * @param route
   * @param callback
   * @param permissionMenuList
   * @returns {Boolean}
   */

  public hasMenuPermission<T = Record<string, unknown>>(
    route: T,
    callback: PermissionCodeCallback<T>,
    permissionMenuList: Pick<BasicResourceItem, 'reCode'>[],
  ): boolean {
    /**
     * you can give us anything through a callback function
     * when we call it, we will get a string or array to
     * determine whether the user has permission
     */
    const permissionCode = callback(route)

    return !permissionCode || permissionMenuList.some((item) => item.reCode === permissionCode)
  }

  /**
   * 获取用户在应用下的菜单权限列表
   * 规则：如果父节点不是菜单类型，过滤的时候会直接去除该节点，不论下面的子节点中是否有菜单选项
   * 统一权限菜单配置规则：菜单嵌套菜单
   *
   * @param {Object} opts 参数
   * @returns {Promise<BasicResourceItem[]>} 菜单列表
   */
  public async getMenuList(
    opts?: ResRequestOptions,
    moduleCode?: string,
  ): Promise<BasicResourceItem[]> {
    const menuTree = await this.getMenuTree(opts, moduleCode)

    return filterAndFlatResources(menuTree, ResourceType.MENU)
  }

  /**
   * filter async routes by recursion
   *
   * @param {*} permissionCodeMap
   * @param {*} routes
   * @param {*} callback
   * @param {*} opts
   */
  #filterRoutesByPermission<T = Record<string, unknown>>(
    permissionCodeMap: Record<string, number>,
    routes: T[],
    callback: PermissionCodeCallback<T>,
    opts: {
      childrenKey: keyof T
      sort: boolean | CompareFn<T>
    },
  ): T[] {
    const newRoutes: T[] = []

    const { childrenKey, sort } = opts

    for (const route of routes) {
      const permissionCode = callback(route)

      if (!permissionCode || permissionCodeMap[permissionCode] !== undefined) {
        let children = route[childrenKey] as T[]

        if (Array.isArray(children) && children.length > 0) {
          children = this.#filterRoutesByPermission(permissionCodeMap, children, callback, opts)
        }

        newRoutes.push({ ...route, [childrenKey]: children })
      }
    }

    if (sort) {
      newRoutes.sort(
        typeof sort === 'function'
          ? sort
          : (a, b) => {
              const aCode = callback(a)
              const bCode = callback(b)

              if (!aCode || !bCode) return 0

              return (permissionCodeMap[aCode] || 0) - (permissionCodeMap[bCode] || 0)
            },
      )
    }

    return newRoutes
  }

  /**
   * 通过菜单列表过滤路由列表
   *
   * @param routeList 路由列表
   * @param menuList 菜单列表
   * @param callback 获取路由中配置的 permissionCode
   * @param opts childrenKey: 子节点的key, sort: 是否排序
   * @returns
   */
  public filterRouteList<T = Record<string, unknown>>(
    routeList: T[],
    menuList: Pick<BasicResourceItem, 'reCode' | 'uiSort'>[],
    callback: PermissionCodeCallback<T>,
    opts: RouteFilterOptions<T> = {},
  ): T[] {
    const { childrenKey = 'children', sort = false } = opts

    const permissionCodeMap: Record<string, number> = Object.fromEntries(
      menuList.map((menu) => [menu.reCode, menu.uiSort]),
    )

    return this.#filterRoutesByPermission<T>(permissionCodeMap, routeList, callback, {
      childrenKey: childrenKey as keyof T,
      sort,
    })
  }

  /**
   * 获取权限路由列表
   *
   * @param routeList
   * @param callback 获取项目中配置的permissionCode
   * @param childrenKey
   * @param opts
   * @returns
   */
  public async getFilteredRouteList<T = Record<string, unknown>>(
    routeList: T[],
    callback: PermissionCodeCallback<T>,
    opts: ResRequestOptions & RouteFilterOptions<T> = {},
  ): Promise<T[]> {
    const { childrenKey = 'children', sort = false, ...reqOptions } = opts

    const menuList = await this.getMenuList(reqOptions)

    return this.filterRouteList<T>(routeList, menuList, callback, {
      childrenKey: childrenKey as keyof T,
      sort,
    })
  }

  /**
   * 获取用户在应用下的的所有角色 - 包含默认角色
   *
   * @param {ResRequestOptions} [opts]
   *
   * @returns {Promise<RoleItem[]>}
   */
  public async getRoleList(opts?: ResRequestOptions): Promise<RoleItem[]> {
    if (!opts && typeof this.#storage.getRoleList === 'function') {
      const list = this.#storage.getRoleList()

      if (list) return list
    }

    const params = this.#getRequestParams(opts)

    const roleList = await this.#request.post<RoleItem[]>(
      PERM_USER_ROLES_PATH,
      {
        lang: params.lang,
        jobNumber: params.employeeNo,
        appCode: this.#appConfig.permCode,
        password: this.#appConfig.permKey,
      },
      {
        headers: {
          [HEADER_AUTH]: params.rtoken,
          [HEADER_EMP_NO]: params.employeeNo,
          [HEADER_LANGID]: params.lang,
          [HEADER_SYS_CODE]: this.#appConfig.permCode,
          [HEADER_RTOKEN]: params.utoken,
        },
      },
    )

    const data = roleList || []

    if (!opts && typeof this.#storage.setRoleList === 'function') {
      this.#storage.setRoleList(data)
    }

    return data
  }

  /**
   * 获取用户在应用下的角色 - 不含默认角色
   *
   * @param {ResRequestOptions} [opts]
   *
   * @returns ｛Promise<RoleItem[]>｝
   */
  public async getBaseRoleList(opts?: ResRequestOptions): Promise<RoleItem[]> {
    if (!opts && typeof this.#storage.getBaseRoleList === 'function') {
      const list = this.#storage.getBaseRoleList()

      if (list) return list
    }

    const params = this.#getRequestParams(opts)

    const roleList = await this.#request.post<RoleItem[]>(
      PERM_USER_ROLES2_PATH,
      {
        lang: params.lang,
        jobNumber: params.employeeNo,
        appCode: this.#appConfig.permCode,
        password: this.#appConfig.permKey,
      },
      {
        headers: {
          [HEADER_AUTH]: params.rtoken,
          [HEADER_EMP_NO]: params.employeeNo,
          [HEADER_LANGID]: params.lang,
          [HEADER_SYS_CODE]: this.#appConfig.permCode,
          [HEADER_RTOKEN]: params.utoken,
        },
      },
    )

    const data = roleList || []

    if (!opts && typeof this.#storage.setBaseRoleList === 'function') {
      this.#storage.setBaseRoleList(data)
    }

    return data
  }

  /**
   * 判断用户是否是某个角色
   *
   * @param {string} roleCode 角色编码
   * @param {ResRequestOptions} [opts]
   *
   * @returns {Promise<boolean>}
   */
  public async hasRole(roleCode: string, opts?: ResRequestOptions): Promise<boolean> {
    const roleList = await this.getRoleList(opts)

    return roleList.some((item) => item.roleCode === roleCode)
  }
}
